import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import io from "socket.io-client";
import Login from "./Login";
import Register from "./Register";
import LandingPage from "./LandingPage";
import Home from "./Home";
import Profile from "./Profile";
import ProfileSetup from "./ProfileSetup";
import AuthCallback from "./AuthCallback";
import WelcomeScreen from "./WelcomeScreen";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PWAUpdateNotification from "./components/PWAUpdateNotification";
import AudioCall from "./components/AudioCall";
import VideoCall from "./components/VideoCall";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import config from "./config";
import { subscribeToPush, handleNotificationMessages, getNotificationPermission } from "./utils/pushNotifications";
import './App.css';

// IMPORTANT: Production reliability
// - Prefer websocket (avoids proxy long-polling issues)
// - Aggressive reconnection (group calls depend on stable signaling)..
const socket = io(config.socketUrl, {
  transports: ['websocket', 'polling'],
  withCredentials: true,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 500,
  reconnectionDelayMax: 2000,
  timeout: 20000,
});

function App() {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [to, setTo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Ensure messages is always an array to prevent runtime errors
  const safeMessages = Array.isArray(messages) ? messages : [];
  
  // Debug logging to help identify the issue
  if (!Array.isArray(messages)) {
    console.warn('App component has non-array messages state:', messages, typeof messages);
  }
  const [text, setText] = useState("");
  const [showSplash, setShowSplash] = useState(true);
  // While we verify an ATOZAS SSO session (HttpOnly cookie), keep showing the
  // splash instead of the landing/login page. This prevents any login UI or
  // "Continue" button from flashing when a user arrives via a valid SSO login.
  const [ssoChecking, setSsoChecking] = useState(
    () => !localStorage.getItem("chatapp_user")
  );
  const [blockStatus, setBlockStatus] = useState({
    isFromBlockedByTo: false,
    isToBlockedByFrom: false,
    canSendMessage: true
  });

  // Splash screen effect
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Load user from localStorage on app start
  useEffect(() => {
    const loadPrivacyInto = (userData) => {
      axios.get(`${config.apiUrl}/api/auth/privacy-settings/${userData.id}`)
        .then(res => {
          const updatedUser = {
            ...userData,
            privacySettings: res.data.privacySettings
          };
          setUser(updatedUser);
          // Update localStorage with privacy settings
          localStorage.setItem("chatapp_user", JSON.stringify(updatedUser));
        })
        .catch(err => {
          console.error('Failed to load privacy settings:', err);
          // Continue with user data without privacy settings
        });
    };

    const savedUser = localStorage.getItem("chatapp_user");
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      // Normalize user id shape across deployments (some responses use `_id`)
      if (userData && !userData.id && userData._id) {
        userData.id = userData._id;
      }
      setUser(userData);
      loadPrivacyInto(userData);
      return;
    }

    // No local user yet. Always try to bootstrap this project's OWN HttpOnly
    // session (created by /auth/atozas/callback after the company-website
    // login). We attempt this regardless of the build-time SSO flag so a valid
    // SSO session ALWAYS signs the user in - even if this particular build was
    // not compiled with REACT_APP_ATOZAS_SSO_ENABLED=true. When there is no
    // session the endpoint returns 401/404 and we simply fall through to the
    // normal login page.
    axios.get(`${config.apiUrl}/auth/atozas/me`, { withCredentials: true })
      .then(res => {
        const userData = res.data?.user;
        if (!userData) throw new Error('no_session_user');
        if (!userData.id && userData._id) userData.id = userData._id;
        localStorage.setItem("chatapp_user", JSON.stringify(userData));
        setUser(userData);
        loadPrivacyInto(userData);
      })
      .catch(() => {
        // No ATOZAS session. Optionally send the visitor to sign in.
        if (config.atozasSso?.autoRedirect) {
          window.location.href = `${config.apiUrl}/auth/atozas`;
        }
      })
      .finally(() => {
        setSsoChecking(false);
      });
  }, []);

  // Initialize push notifications when user is logged in
  useEffect(() => {
    const initializePushNotifications = async () => {
      if (!user) return;

      try {
        //console.log('Initializing push notifications for user:', user.username);
        
        // Subscribe to push notifications
        await subscribeToPush(user.id);
        
        //console.log('Push notifications initialized successfully');
      } catch (error) {
        console.error('Failed to initialize push notifications:', error);
        
        // Try to get permission at least
        try {
          await getNotificationPermission();
        } catch (permError) {
          console.error('Failed to get notification permission:', permError);
        }
      }
    };

    initializePushNotifications();
  }, [user]);

  // Handle notification clicks
  useEffect(() => {
    handleNotificationMessages((data) => {
      //console.log('Notification clicked:', data);
      
      if (data.action === 'reply' && data.data?.type === 'message') {
        // Navigate to chat with sender
        if (data.data.from) {
          setTo(data.data.from);
          setSelectedGroup(null);
        }
      } else if ((data.action === 'reply' || data.action === 'view') && data.data?.type === 'group_message') {
        // Navigate to group chat
        if (data.data.groupId) {
          // Fetch group details and set as selected
          axios.get(`${config.apiUrl}/groups/${data.data.groupId}`)
            .then(response => {
              setSelectedGroup(response.data);
              setTo(null);
            })
            .catch(error => {
              console.error('Error loading group from notification:', error);
            });
        }
      } else if (data.action === 'accept' && data.data?.type === 'friend_request') {
        // Handle friend request acceptance
        //console.log('Accept friend request:', data.data.contactId);
      } else if (data.action === 'view') {
        // Just focus the app
        window.focus();
      }
    });
  }, []);

  useEffect(() => {
    if (user) {
      const userId = user.id || user._id;
      if (!userId) {
        console.error('❌ Missing user id for socket join/userActivity', user);
        return;
      }
      // Join socket room immediately
      socket.emit("join", userId);
      
      // Handle socket reconnection - rejoin on reconnect
      const handleReconnect = () => {
        console.log('🔄 Socket reconnected, rejoining user room...');
        socket.emit("join", userId);
      };
      
      // Handle connection events
      socket.on('connect', () => {
        console.log('✅ Socket connected');
        socket.emit("join", userId);
      });
      
      socket.on('reconnect', handleReconnect);
      
      // Set up activity tracking
      const sendActivity = () => {
        socket.emit("userActivity", userId);
      };
      
      // Send activity immediately
      sendActivity();
      
      // Send activity every 30 seconds while user is active
      const activityInterval = setInterval(sendActivity, 30000);
      
      // Send activity on user interactions
      const handleUserActivity = () => {
        sendActivity();
      };
      
      // Add event listeners for user activity
      document.addEventListener('click', handleUserActivity);
      document.addEventListener('keypress', handleUserActivity);
      document.addEventListener('scroll', handleUserActivity);
      document.addEventListener('mousemove', handleUserActivity);
      
      // Cleanup function
      const cleanup = () => {
        clearInterval(activityInterval);
        document.removeEventListener('click', handleUserActivity);
        document.removeEventListener('keypress', handleUserActivity);
        document.removeEventListener('scroll', handleUserActivity);
        document.removeEventListener('mousemove', handleUserActivity);
        socket.off('connect');
        socket.off('reconnect', handleReconnect);
      };
      
      // Send final activity update when user leaves
      window.addEventListener('beforeunload', sendActivity);
      
      // Fetch users with their last messages
      axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
        .then(res => {
          setUsers(res.data);
        })
        .catch(err => {
          console.error('Failed to fetch users with messages:', err);
          // Fallback to old API if new one fails
          axios.get(`${config.apiUrl}/api/auth/users`)
            .then(res => {
              const filteredUsers = res.data.filter(u => u.id !== user.id);
              setUsers(filteredUsers);
            })
            .catch(err => {
              console.error('Failed to fetch users:', err);
              setUsers([]);
            });
        });
      
      // Return cleanup function
      return () => {
        cleanup();
        window.removeEventListener('beforeunload', sendActivity);
      };
    }
  }, [user]);

  useEffect(() => {
    socket.on("receiveMessage", (msg) => {
      if ((msg.from === user?.id && msg.to === to) || (msg.from === to && msg.to === user?.id)) {
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [msg];
          return [...prev, msg];
        });
      }
      
      // Update user list to show new last message
      if (user) {
        axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
          .then(res => {
            setUsers(res.data);
          })
          .catch(err => {
            console.error('Failed to refresh users with messages:', err);
          });
      }

      // If the current chat is active and the message is incoming, mark as delivered and read immediately
      if (user && to && msg.from === to && msg.to === user.id) {
        // Emit via socket to update sender's ticks instantly
        socket.emit("messageDelivered", { from: user.id, to });
        socket.emit("messageRead", { from: user.id, to });
      }
    });

    // Handle message delivery status updates
    socket.on("messagesDelivered", (data) => {
      //console.log('Received messagesDelivered event:', data);
      const pertainsToCurrentChat =
        (data.from === user?.id && data.to === to) ||
        (data.from === to && data.to === user?.id);

      if (pertainsToCurrentChat) {
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [];
          const updated = prev.map(msg => 
            msg.from === data.from && msg.to === data.to && msg.status === 'sent'
              ? { ...msg, status: 'delivered', deliveredAt: new Date() }
              : msg
          );
          return updated;
        });
        // Refresh users list to update unread counts/last message status
        if (user) {
          axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
            .then(res => setUsers(res.data))
            .catch(err => console.error('Failed to refresh users with messages:', err));
        }
      }
    });

    // Handle message read status updates
    socket.on("messagesRead", (data) => {
      //console.log('Received messagesRead event:', data);
      const pertainsToCurrentChat =
        (data.from === user?.id && data.to === to) ||
        (data.from === to && data.to === user?.id);

      if (pertainsToCurrentChat) {
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [];
          const updated = prev.map(msg => 
            msg.from === data.from && msg.to === data.to && (msg.status === 'sent' || msg.status === 'delivered')
              ? { ...msg, status: 'read', readAt: new Date() }
              : msg
          );
          return updated;
        });
        // Refresh users list to reflect read status in sidebar///
        if (user) {
          axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
            .then(res => setUsers(res.data))
            .catch(err => console.error('Failed to refresh users with messages:', err));
        }
      }
    });

    // Handle message blocking events
    socket.on("messageBlocked", (data) => {
      //console.log('Message blocked:', data);
      alert(data.message);
    });

    socket.on("messageError", (data) => {
      //console.log('Message error:', data);
      alert(data.message);
    });

    // Handle real-time block status changes
    socket.on("blockStatusChanged", (data) => {
      //console.log('Block status changed:', data);
      
      // Update block status immediately for current chat
      if (user && to) {
        if (data.type === 'blocked' && data.targetUserId === to) {
          // Current user blocked the person they're chatting with
          setBlockStatus(prev => ({
            ...prev,
            isToBlockedByFrom: true,
            canSendMessage: false
          }));
        } else if (data.type === 'blockedBy' && data.blockerId === to) {
          // Current user was blocked by the person they're chatting with
          setBlockStatus(prev => ({
            ...prev,
            isFromBlockedByTo: true,
            canSendMessage: false
          }));
        } else if (data.type === 'unblocked' && data.targetUserId === to) {
          // Current user unblocked the person they're chatting with
          setBlockStatus(prev => ({
            ...prev,
            isToBlockedByFrom: false,
            canSendMessage: true
          }));
        } else if (data.type === 'unblockedBy' && data.unblockerId === to) {
          // Current user was unblocked by the person they're chatting with
          setBlockStatus(prev => ({
            ...prev,
            isFromBlockedByTo: false,
            canSendMessage: true
          }));
        }
      }
      
      // Show immediate notification with better styling
      let message = '';
      let type = 'info';
      
      if (data.type === 'blockedBy') {
        message = `You have been blocked by ${data.blockerUsername}`;
        type = 'warning';
      } else if (data.type === 'blocked') {
        message = `You have blocked ${data.targetUsername}`;
        type = 'success';
      } else if (data.type === 'unblockedBy') {
        message = `You have been unblocked by ${data.unblockerUsername}`;
        type = 'success';
      } else if (data.type === 'unblocked') {
        message = `You have unblocked ${data.targetUsername}`;
        type = 'info';
      }
      
      // Show notification
      if (message) {
        // Create a custom notification element
        const notification = document.createElement('div');
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${type === 'warning' ? '#ff6b6b' : type === 'success' ? '#51cf66' : '#339af0'};
          color: white;
          padding: 16px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          font-weight: 500;
          max-width: 300px;
          word-wrap: break-word;
          animation: slideIn 0.3s ease-out;
        `;
        notification.textContent = message;
        
        // Add animation styles
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `;
        document.head.appendChild(style);
        
        document.body.appendChild(notification);
        
        // Auto-remove after 4 seconds
        setTimeout(() => {
          notification.style.animation = 'slideIn 0.3s ease-out reverse';
          setTimeout(() => {
            if (notification.parentNode) {
              notification.parentNode.removeChild(notification);
            }
          }, 300);
        }, 4000);
      }
      
      // Refresh users list to update UI
      if (user) {
        setTimeout(() => {
          refreshUsers(true);
        }, 1000);
      }
    });

    return () => {
      socket.off("receiveMessage");
      socket.off("messagesDelivered");
      socket.off("messagesRead");
      socket.off("messageBlocked");
      socket.off("messageError");
      socket.off("blockStatusChanged");
    };
  }, [user, to]);

  // Persist a per-conversation call-history entry (additive feature).
  // To guarantee a single record shown to both participants, only the caller
  // (outgoing) side writes it; the backend dedupes by callId. Multi-participant
  // calls are intentionally skipped (this is a 1:1 chat-timeline feature).
  const persistCallHistory = (callData) => {
    try {
      if (!callData || callData.isMultiParticipant) return;
      if (callData.direction !== 'outgoing') return;
      if (!callData.userId || !user?.id) return;
      socket.emit('logCallHistory', {
        callId: callData.callId || null,
        from: user.id,
        to: callData.userId,
        callType: callData.type === 'video' ? 'video' : 'audio',
        status: callData.status || 'missed',
        duration: callData.duration || 0,
      });
    } catch (err) {
      console.error('Failed to persist call history:', err);
    }
  };

  const fetchMessages = async (toId) => {
    setTo(toId);
    const res = await axios.get(`${config.apiUrl}/api/messages/${user.id}/${toId}`);
    
    // Handle new API response format with block status
    if (res.data.messages && res.data.blockStatus) {
      // Ensure messages is an array
      const messagesArray = Array.isArray(res.data.messages) ? res.data.messages : [];
      setMessages(messagesArray);
      setBlockStatus(res.data.blockStatus);
    } else {
      // Fallback for old API format
      const messagesArray = Array.isArray(res.data) ? res.data : [];
      setMessages(messagesArray);
      setBlockStatus({
        isFromBlockedByTo: false,
        isToBlockedByFrom: false,
        canSendMessage: true
      });
    }
    
    // Refresh user list to update last message and unread count
    axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
      .then(res => {
        setUsers(res.data);
      })
      .catch(err => {
        console.error('Failed to refresh users with messages:', err);
      });
    
    // Only mark messages as delivered/read if communication is allowed
    if (res.data.blockStatus?.canSendMessage !== false) {
      const messagesToProcess = res.data.messages || res.data;
      setTimeout(() => {
        const sentMessages = messagesToProcess.filter(msg => 
          msg.from === toId && msg.to === user.id && msg.status === 'sent'
        );
        const unreadMessages = messagesToProcess.filter(msg => 
          msg.from === toId && msg.to === user.id && (msg.status === 'sent' || msg.status === 'delivered')
        );
        
        if (sentMessages.length > 0) {
          //console.log('Fetch: Marking messages as delivered:', sentMessages.length, 'messages');
          markMessagesAsDelivered(user.id, toId);
        }
        
        if (unreadMessages.length > 0) {
          //console.log('Fetch: Marking messages as read:', unreadMessages.length, 'messages');
          markMessagesAsRead(user.id, toId);
        }
      }, 200); // Small delay to ensure state is updated
    }
  };

  const sendMessage = (options = {}) => {
    socket.emit("sendMessage", { from: user.id, to, text, replyTo: options.replyTo });
    setText("");
    
    // Update user list to show new last message
    setTimeout(() => {
      if (user) {
        axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
          .then(res => {
            setUsers(res.data);
          })
          .catch(err => {
            console.error('Failed to refresh users with messages:', err);
          });
      }
    }, 100); // Small delay to ensure message is saved
  };

  const handleVoiceMessage = async (audioFile, audioUrl) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('voice', audioFile);

      // Upload voice file
      const uploadResponse = await axios.post(`${config.apiUrl}/api/auth/upload-voice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { voiceUrl } = uploadResponse.data;

      // Get audio duration
      const audio = new Audio(audioUrl);
      let duration = 0;
      
      audio.addEventListener('loadedmetadata', () => {
        duration = Math.round(audio.duration);
        
        // Send voice message
        socket.emit("sendMessage", { 
          from: user.id, 
          to, 
          text: '', 
          messageType: 'voice',
          voiceUrl: voiceUrl,
          voiceDuration: duration
        });

        // Update user list to show new last message
        setTimeout(() => {
          if (user) {
            axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
              .then(res => {
                setUsers(res.data);
              })
              .catch(err => {
                console.error('Failed to refresh users with messages:', err);
              });
          }
        }, 100);
      });

      audio.addEventListener('error', () => {
        // If duration can't be determined, send without it
        socket.emit("sendMessage", { 
          from: user.id, 
          to, 
          text: '', 
          messageType: 'voice',
          voiceUrl: voiceUrl,
          voiceDuration: 0
        });
      });

    } catch (error) {
      console.error('Error sending voice message:', error);
      alert('Failed to send voice message. Please try again.');
    }
  };

  const handleGroupVoiceMessage = async (audioFile, audioUrl, groupReplyTo = null) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('voice', audioFile);

      // Upload voice file first
      const uploadResponse = await axios.post(`${config.apiUrl}/api/auth/upload-voice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { voiceUrl } = uploadResponse.data;

      // Get audio duration
      const audio = new Audio(audioUrl);
      let duration = 0;
      
      audio.addEventListener('loadedmetadata', () => {
        duration = Math.round(audio.duration);
        
        // Send voice message to group via socket
        socket.emit("sendGroupMessage", { 
          from: user.id, 
          groupId: selectedGroup._id,
          text: '', 
          messageType: 'voice',
          voiceUrl: voiceUrl,
          voiceDuration: duration,
          replyTo: groupReplyTo?._id || null
        });

        // Update group list to show new last message
        setTimeout(() => {
          if (user) {
            axios.get(`${config.apiUrl}/api/groups/user/${user.id}`)
              .then(res => {
                // Update groups in state if needed
                //console.log('Groups refreshed after voice message');
              })
              .catch(err => {
                console.error('Failed to refresh groups:', err);
              });
          }
        }, 100);
      });

      audio.addEventListener('error', () => {
        // If duration can't be determined, send without it
        socket.emit("sendGroupMessage", { 
          from: user.id, 
          groupId: selectedGroup._id,
          text: '', 
          messageType: 'voice',
          voiceUrl: voiceUrl,
          voiceDuration: 0,
          replyTo: groupReplyTo?._id || null
        });
      });

    } catch (error) {
      console.error('Error sending group voice message:', error);
      alert('Failed to send voice message to group. Please try again.');
    }
  };

  // Function to mark messages as delivered
  const markMessagesAsDelivered = (fromId, toId) => {
    socket.emit("messageDelivered", { from: fromId, to: toId });
  };

  // Function to mark messages as read
  const markMessagesAsRead = (fromId, toId) => {
    socket.emit("messageRead", { from: fromId, to: toId });
  };

  const handleLogout = () => {
    // Destroy THIS project's HttpOnly session (best-effort). This logs the user
    // out of THIS project only; the company website session is untouched.
    axios.post(`${config.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .catch(() => { /* ignore: local logout still proceeds */ });
    setUser(null);
    setTo("");
    setMessages([]);
    setText("");
    localStorage.removeItem("chatapp_user");
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  const refreshUsers = (forceRefresh = false) => {
    if (user) {
      // Add cache-busting parameter if force refresh is requested
      const url = forceRefresh 
        ? `${config.apiUrl}/api/auth/users-with-messages/${user.id}?t=${Date.now()}`
        : `${config.apiUrl}/api/auth/users-with-messages/${user.id}`;
        
      axios.get(url)
        .then(res => {
          setUsers(res.data);
        })
        .catch(err => {
          console.error('Failed to refresh users with messages:', err);
        });
    }
  };

  if (showSplash || ssoChecking) {
    return <WelcomeScreen />;
  }

  return (
    <>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={
              user ? <Navigate to="/" replace /> : 
              <Login setUser={setUser} />
            } 
          />
          <Route 
            path="/register" 
            element={
              user ? <Navigate to="/" replace /> : 
              <Register setUser={setUser} />
            } 
          />
          <Route 
            path="/auth/callback" 
            element={<AuthCallback setUser={setUser} />} 
          />
          <Route 
            path="/profile-setup" 
            element={<ProfileSetup setUser={setUser} />} 
          />
          <Route 
            path="/terms" 
            element={
              <TermsAndConditions onBack={() => window.history.back()} />
            } 
          />
          <Route 
            path="/privacy-policy" 
            element={
              <PrivacyPolicy onBack={() => window.history.back()} />
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/" 
            element={user ? <Navigate to="/chats" replace /> : <LandingPage />}
          />
          <Route 
            path="/chats" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/contacts" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/status" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/groups" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/calls" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/settings" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route
            path="/starred-messages"
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route 
            path="/pricing" 
            element={
              user ? (
                <Home
                  user={user}
                  users={users}
                  setUsers={setUsers}
                  to={to}
                  setTo={setTo}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  messages={safeMessages}
                  setMessages={setMessages}
                  text={text}
                  setText={setText}
                  fetchMessages={fetchMessages}
                  sendMessage={sendMessage}
                  handleVoiceMessage={handleVoiceMessage}
                  handleGroupVoiceMessage={handleGroupVoiceMessage}
                  markMessagesAsDelivered={markMessagesAsDelivered}
                  markMessagesAsRead={markMessagesAsRead}
                  socket={socket}
                  refreshUsers={refreshUsers}
                  blockStatus={blockStatus}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/profile" 
            element={
              user ? (
                <Profile 
                  user={user} 
                  onLogout={handleLogout} 
                  onUserUpdate={handleUserUpdate} 
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      {/* Global call components — these are the ONLY mounts of AudioCall /
          VideoCall in the whole app. The chat-header buttons in Home.js are
          thin triggers that dispatch `requestStartAudioCall` /
          `requestStartVideoCall`; mounting these components only once
          prevents duplicate socket listeners that would otherwise show the
          incoming-call modal twice and re-show it after hangup. We forward
          end-of-call data via a `callLogEntry` window event so Home.js can
          persist it in call history without owning the components. */}
      {user && (
        <>
          <AudioCall
            user={user}
            selectedUser={null}
            onEndCall={(callData) => {
              if (callData) {
                window.dispatchEvent(new CustomEvent('callLogEntry', { detail: callData }));
                persistCallHistory(callData);
              }
            }}
            socket={socket}
          />
          <VideoCall
            user={user}
            selectedUser={null}
            onEndCall={(callData) => {
              if (callData) {
                window.dispatchEvent(new CustomEvent('callLogEntry', { detail: callData }));
                persistCallHistory(callData);
              }
            }}
            socket={socket}
          />
        </>
      )}
      <PWAInstallPrompt />
      <PWAUpdateNotification />
    </>
  );
}

export default App; 